import { ComponentType, PropsWithChildren, ReactNode } from 'react';

import {
  Extension,
  attachComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

export type ItemRenderExtensionOptions<TItem> = {
  id: string;
  priority?: number;
  predicate: (item: TItem) => boolean;
  render: (item: TItem) => ReactNode;
};

// Extension keys for component data attachment
const ITEM_RENDER_EXTENSION_KEY = 'confidence.item-render-extension';
const ITEM_RENDER_EXTENSION_WRAPPER_KEY =
  'confidence.item-render-extension-wrapper';

export type ItemRenderExtensionComponent<_TReturnValue, _TInputProps> =
  () => null;

const extensionsStorage = new WeakMap<ComponentType, unknown[]>();

export const createItemRenderExtension = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TOptions accepts variant ItemRenderExtensionOptions<TItem>; T is contravariant via predicate/render so unknown rejects all consumers
  TOptions extends ItemRenderExtensionOptions<any>,
  TReturnValue = unknown,
  TInputProps = unknown,
>(
  options: TOptions,
): Extension<ItemRenderExtensionComponent<TReturnValue, TInputProps>> => {
  return {
    expose() {
      const ActivityItemExtensionDataHolder = () => null;

      attachComponentData(
        ActivityItemExtensionDataHolder,
        ITEM_RENDER_EXTENSION_KEY,
        options,
      );

      return ActivityItemExtensionDataHolder;
    },
  };
};

export const createItemRenderExtensions = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TOptions accepts variant ItemRenderExtensionOptions<TItem>; T is contravariant via predicate/render so unknown rejects all consumers
  TOptions extends ItemRenderExtensionOptions<any>,
>(options: {
  id: string;
}): ComponentType<PropsWithChildren> => {
  const ItemRenderExtensionsWrapper: ComponentType<PropsWithChildren> = ({
    children,
  }): JSX.Element | null => {
    // Extract extensions from children
    const extensions = useElementFilter(children, elements =>
      elements.findComponentData<TOptions>({
        key: ITEM_RENDER_EXTENSION_KEY,
      }),
    );

    // Store extensions on the wrapper component
    extensionsStorage.set(ItemRenderExtensionsWrapper, extensions);

    return null;
  };

  // Use a unique wrapper key for each category to enable filtering
  const categoryWrapperKey = `${ITEM_RENDER_EXTENSION_WRAPPER_KEY}.${options.id}`;

  attachComponentData(ItemRenderExtensionsWrapper, categoryWrapperKey, true);

  return ItemRenderExtensionsWrapper;
};

export const useItemRenderExtensions = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TOptions accepts variant ItemRenderExtensionOptions<TItem>; T is contravariant via predicate/render so unknown rejects all consumers
  TOptions extends ItemRenderExtensionOptions<any>,
  TComponentDataType = TOptions,
>(
  wrapper: ComponentType<PropsWithChildren>,
): TComponentDataType[] => {
  // Get the stored extensions from the wrapper component
  return (extensionsStorage.get(wrapper) as TComponentDataType[]) || [];
};
